/**
 * Dot painting — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_paint_crafts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const dotPaintingPack: ToilePack = {
    slug: 'dot-painting',
    category: 'craft',
    name: 'Dot painting',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'mandala', label: 'Mandala', subject: 'a dot mandala on a black round canvas'},
        {key: 'flower', label: 'Flower', subject: 'a dot painted flower'},
        {key: 'landscape', label: 'Landscape', subject: 'a dot painted desert landscape'},
    ],
    styles: [
        {key: 'rainbow', label: 'Rainbow', style: 'acrylic dot art made with dotting tools, raised glossy dots graded from large to tiny', palette: 'saturated rainbow colors', framing: 'laid flat on a pale wooden table, seen from directly above with an even margin around it'},
        {key: 'earthy', label: 'Earth tones', style: 'dot art in matte acrylic dots packed in tight rows and rings', palette: 'ochre, rust, cream and white', framing: 'propped upright on a linen cloth among a few smooth river stones, seen near eye level'},
        {key: 'metallic', label: 'Metallic', style: 'pearl-like raised dots of metallic paint with a soft sheen', palette: 'white, silver and gold dots only', framing: 'on a dark slate surface, seen from above at a slight angle, close enough to read every dot'},
    ],
    directions: ['on a rock', 'ocean colors', 'a butterfly'],
    directionHint: 'try: on a rock',
    seo: {
        title: 'Dot painting: mandala, flower, desert landscape',
        metaDescription: 'Nine dot painting designs, a mandala, a flower and a desert landscape, in rainbow, earth-tone and metallic dots.',
        h1: 'Dot painting designs: mandala, flower, landscape',
        tagline: 'Three motifs, each in three palettes: rainbow dots, earth-tone dots and metallic dots.',
        intro: 'Dot painting builds a picture from raised dots of acrylic, placed with dotting tools. These nine show three motifs, a mandala on a black round canvas, a dot-painted flower and a desert landscape, each in three palettes: rainbow, earth tones and metallic. Each is shown flat on a table, propped on linen beside stones, or close up on dark slate. Type a subject, such as a butterfly or a painted rock, to ask for it.',
        useCases: [
            'a canvas for a bedroom wall',
            'a painted stone for a garden',
            'a gift painted on a round board',
            'a practice piece for dotting technique',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'Three motifs, a mandala, a flower and a desert landscape, each in a rainbow, earth-tone or metallic palette.'},
            {q: 'Are these photos of real dot paintings?', a: "No. They are generated pictures of each design, not photographs of one artist's work."},
            {q: 'What is the difference between the palettes?', a: 'Rainbow uses saturated colours, earth tones use ochre, rust, cream and white, and metallic uses only white, silver and gold dots.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. Type a subject such as ocean colours, a butterfly or a rock. A typed subject is a request, not a promised result.'},
        ],
    },
}
